import { yupResolver } from "@hookform/resolvers/yup";
import { Box, Button, Divider, Grid, RadioGroup, Stack, Typography } from "@mui/material";
import { Drawer } from "@zfp-akademie-a-s/zfp-design";
import { FormSelect } from "@zfp-akademie-a-s/zfp-design/form";
import { AnalysisDetailContext } from "Components/Analyses/Detail/AnalysisDetailContext";
import { type DrawerIncomeFormType, getDrawerIncomeFormSchema } from "Forms/Schemas/Budget/Incomes/drawerIncomeFormSchema";
import { type Option, toSelectOptions } from "Forms/fieldOptions";
import { type AnalysisClientItem, type Income, IncomeIncomeType } from "Infrastructure/ApiZfp/Schema";
import { Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { formatClientName } from "Infrastructure/Utils/ClientUtils";
import { type FC, useContext, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { DisabilityIncomeForm } from "../Forms/DisabilityIncomeForm";
import { EmploymentIncomeForm } from "../Forms/EmploymentIncomeForm";
import { OrphanOrWidowIncomeForm } from "../Forms/OrphanOrWidowIncomeForm";
import { OtherIncomeForm } from "../Forms/OtherIncomeForm";
import { ParentalIncomeForm } from "../Forms/ParentalIncomeForm";
import { PensionIncomeForm } from "../Forms/PensionIncomeForm";
import { RentalIncomeForm } from "../Forms/RentalIncomeForm";
import { SelfEmploymentIncomeForm } from "../Forms/SelfEmploymentIncomeForm";
import { IncomeTypeTile } from "./IncomeTypeTile";

type Props = {
  income: Income;
  onClose: () => void;
  clients?: AnalysisClientItem[];
  readonly open: boolean;
};

export type SubmitIncomeFormHandle = {
  readonly submitForm: () => void;
  readonly deleteIncome: () => void;
};

export const EditIncomeDrawer: FC<Props> = ({ income, onClose, clients, open }) => {
  const { readOnly } = useContext(AnalysisDetailContext);

  const { t } = useResource();
  const [incomeType, setIncomeType] = useState<IncomeIncomeType>(income.incomeType);

  const employmentIncomeFormRef = useRef<SubmitIncomeFormHandle>(null);
  const selfEmploymentIncomeFormRef = useRef<SubmitIncomeFormHandle>(null);
  const rentalIncomeFormRef = useRef<SubmitIncomeFormHandle>(null);
  const parentalIncomeFormRef = useRef<SubmitIncomeFormHandle>(null);
  const pensionIncomeFormRef = useRef<SubmitIncomeFormHandle>(null);
  const disabilityIncomeFormRef = useRef<SubmitIncomeFormHandle>(null);
  const orphanOrWidowIncomeFormRef = useRef<SubmitIncomeFormHandle>(null);
  const otherIncomeFormRef = useRef<SubmitIncomeFormHandle>(null);

  const clientOptions: Option[] = (clients ?? []).map((client) => ({
    value: client.guid,
    name: formatClientName(client.firstName, client.surname, client.birthDate ?? null),
  }));

  const { control, watch } = useForm<DrawerIncomeFormType>({
    resolver: yupResolver(getDrawerIncomeFormSchema(t)),
    mode: "onBlur",
    defaultValues: {
      clientId: income.clientGuid,
    },
  });

  const selectedClientId = watch("clientId");

  return (
    <Drawer
      open={open}
      onClose={onClose}
      closeLabel={t(Resources.Common.Close)}
      title={t(Resources.AnalysisDetail.Budget.Incomes.Edit)}
      startAction={
        <Button
          disabled={readOnly}
          variant="text"
          color="error"
          onClick={() => {
            if (incomeType === IncomeIncomeType.employment) {
              employmentIncomeFormRef.current?.deleteIncome();
            }
            if (incomeType === IncomeIncomeType.self_employment) {
              selfEmploymentIncomeFormRef.current?.deleteIncome();
            }
            if (incomeType === IncomeIncomeType.rental) {
              rentalIncomeFormRef.current?.deleteIncome();
            }
            if (incomeType === IncomeIncomeType.parental) {
              parentalIncomeFormRef.current?.deleteIncome();
            }
            if (incomeType === IncomeIncomeType.pension) {
              pensionIncomeFormRef.current?.deleteIncome();
            }
            if (incomeType === IncomeIncomeType.disability) {
              disabilityIncomeFormRef.current?.deleteIncome();
            }
            if (incomeType === IncomeIncomeType.orphan_or_widow_pension) {
              orphanOrWidowIncomeFormRef.current?.deleteIncome();
            }
            if (incomeType === IncomeIncomeType.other) {
              otherIncomeFormRef.current?.deleteIncome();
            }
          }}
        >
          {t(Resources.Common.Delete)}
        </Button>
      }
      primaryAction={
        <Button
          variant="contained"
          disabled={!selectedClientId || readOnly}
          type="submit"
          onClick={() => {
            if (incomeType === IncomeIncomeType.employment) {
              employmentIncomeFormRef.current?.submitForm();
            }
            if (incomeType === IncomeIncomeType.self_employment) {
              selfEmploymentIncomeFormRef.current?.submitForm();
            }
            if (incomeType === IncomeIncomeType.rental) {
              rentalIncomeFormRef.current?.submitForm();
            }
            if (incomeType === IncomeIncomeType.parental) {
              parentalIncomeFormRef.current?.submitForm();
            }
            if (incomeType === IncomeIncomeType.pension) {
              pensionIncomeFormRef.current?.submitForm();
            }
            if (incomeType === IncomeIncomeType.disability) {
              disabilityIncomeFormRef.current?.submitForm();
            }
            if (incomeType === IncomeIncomeType.orphan_or_widow_pension) {
              orphanOrWidowIncomeFormRef.current?.submitForm();
            }
            if (incomeType === IncomeIncomeType.other) {
              otherIncomeFormRef.current?.submitForm();
            }
          }}
        >
          {t(Resources.Common.Save)}
        </Button>
      }
    >
      <Stack spacing={Spacing.XXL} mb={Spacing.XXL}>
        <FormSelect
          control={control}
          name="clientId"
          label={t(Resources.AnalysisDetail.Budget.Incomes.IncomeBelongsTo)}
          required
          options={toSelectOptions(clientOptions)}
          placeholder={t(Resources.Common.SelectPlaceholder)}
          disabled={readOnly}
          fullWidth
        />
        <Divider />
      </Stack>

      <Box mb={Spacing.XXL}>
        <Stack spacing={Spacing.XL}>
          <Typography variant="subtitle1" fontWeight={600}>
            {t(Resources.AnalysisDetail.Budget.Incomes.IncomeType)}
          </Typography>

          <RadioGroup
            aria-label={t(Resources.AnalysisDetail.Budget.Incomes.IncomeType)}
            value={incomeType}
            onChange={(_, value) => {
              setIncomeType(value as IncomeIncomeType);
            }}
            sx={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: Spacing.M }}
          >
            {(Object.values(IncomeIncomeType) as Array<IncomeIncomeType>).map((value) => (
              <IncomeTypeTile key={value} incomeType={value} disabled={value !== incomeType} />
            ))}
          </RadioGroup>
        </Stack>
      </Box>

      <Box mb={Spacing.XXL}>
        {incomeType === IncomeIncomeType.employment && (
          <EmploymentIncomeForm onClose={onClose} clientId={selectedClientId} edit={income} ref={employmentIncomeFormRef} />
        )}
        {incomeType === IncomeIncomeType.self_employment && (
          <SelfEmploymentIncomeForm onClose={onClose} clientId={selectedClientId} edit={income} ref={selfEmploymentIncomeFormRef} />
        )}
        {incomeType === IncomeIncomeType.rental && (
          <RentalIncomeForm onClose={onClose} clientId={selectedClientId} edit={income} ref={rentalIncomeFormRef} />
        )}
        {incomeType === IncomeIncomeType.parental && (
          <ParentalIncomeForm onClose={onClose} clientId={selectedClientId} edit={income} ref={parentalIncomeFormRef} />
        )}
        {incomeType === IncomeIncomeType.pension && (
          <PensionIncomeForm onClose={onClose} clientId={selectedClientId} edit={income} ref={pensionIncomeFormRef} />
        )}
        {incomeType === IncomeIncomeType.disability && (
          <DisabilityIncomeForm onClose={onClose} clientId={selectedClientId} edit={income} ref={disabilityIncomeFormRef} />
        )}
        {incomeType === IncomeIncomeType.orphan_or_widow_pension && (
          <OrphanOrWidowIncomeForm onClose={onClose} clientId={selectedClientId} edit={income} ref={orphanOrWidowIncomeFormRef} />
        )}
        {incomeType === IncomeIncomeType.other && (
          <OtherIncomeForm onClose={onClose} clientId={selectedClientId} edit={income} ref={otherIncomeFormRef} />
        )}
      </Box>
    </Drawer>
  );
};
